import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DeviceRow {
  readonly deviceId: string;
  readonly platform: string;
  readonly osVersion: string | null;
  readonly appVersion: string | null;
}

interface VersionRow {
  readonly key: string;
  readonly appVersion: string;
  readonly platform: string;
  readonly deviceCount: number;
}

/**
 * M22 L22.01 — Mobile App Versions.
 * The app-version landscape in the field, aggregated from the device registry
 * (t281_device_master.app_version) per the GR §E reconciliation — no separate
 * mobile_app_versions table is introduced (no duplicate tables).
 */
export function M22AppVersionsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: DeviceRow[] }>('/v1/mobile/devices', { token })
      .then((r) => { if (alive) setDevices(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const versions = useMemo<VersionRow[] | null>(() => {
    if (!devices) return null;
    const counts = new Map<string, VersionRow>();
    for (const d of devices) {
      const appVersion = d.appVersion ?? 'UNKNOWN';
      const key = `${d.platform}::${appVersion}`;
      const existing = counts.get(key);
      if (existing) {
        counts.set(key, { ...existing, deviceCount: existing.deviceCount + 1 });
      } else {
        counts.set(key, { key, appVersion, platform: d.platform, deviceCount: 1 });
      }
    }
    return Array.from(counts.values()).sort((a, b) => b.deviceCount - a.deviceCount);
  }, [devices]);

  if (error) return <p role="alert">{error}</p>;
  if (!versions) return <p>{t('m22.appVersions.loading')}</p>;
  return (
    <main aria-label="mobile-app-versions">
      <h1>{t('m22.appVersions.title')}</h1>
      {versions.length === 0 ? (
        <p>{t('m22.appVersions.empty')}</p>
      ) : (
        <table aria-label="mobile-app-versions-table">
          <thead>
            <tr><th>{t('m22.appVersions.colAppVersion')}</th><th>{t('m22.appVersions.colPlatform')}</th><th>{t('m22.appVersions.colDevices')}</th></tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.key}>
                <td>{v.appVersion}</td>
                <td>{v.platform}</td>
                <td>{v.deviceCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
